import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card } from 'reactstrap';
import {
    ConsoleLogger,
    DefaultDeviceController,
    DefaultMeetingSession,
    LogLevel,
    MeetingSessionConfiguration,
    VideoTileState
} from 'amazon-chime-sdk-js';
import axios from 'axios';
import Swal from 'sweetalert2';
import ApiService from '../util/ApiService';
import AuthService from '../util/AuthService';

require("./assets/css/Video.css");

const VideoRoom = () => {
    const { attendanceId, callId: urlCallId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = searchParams.get('token');
    const isHost = searchParams.get('host') === 'true';

    const [isLoading, setIsLoading] = useState(true);
    const [isConnected, setIsConnected] = useState(false);
    const [isWaitingForCall, setIsWaitingForCall] = useState(false);
    const [isAudioOn, setIsAudioOn] = useState(true);
    const [isVideoOn, setIsVideoOn] = useState(true);
    const [callDuration, setCallDuration] = useState(0);
    const [attendanceInfo, setAttendanceInfo] = useState(null);
    const [error, setError] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);
    const [isMockMode, setIsMockMode] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [callAudioDevices, setCallAudioDevices] = useState({ inputs: [], outputs: [] });
    const [callSelectedMic, setCallSelectedMic] = useState('');
    const [callSelectedSpeaker, setCallSelectedSpeaker] = useState('');
    const [currentCallId, setCurrentCallId] = useState(urlCallId || null);
    const [audioBlocked, setAudioBlocked] = useState(false);
    const retryInterval = useRef(null);

    // Audio test states
    const [showAudioTest, setShowAudioTest] = useState(true);
    const [audioTestPassed, setAudioTestPassed] = useState(false);
    const [microphoneLevel, setMicrophoneLevel] = useState(0);
    const [isMicWorking, setIsMicWorking] = useState(false);
    const [isSpeakerTested, setIsSpeakerTested] = useState(false);
    const [audioDevices, setAudioDevices] = useState({ inputs: [], outputs: [] });
    const [selectedMic, setSelectedMic] = useState('');
    const [selectedSpeaker, setSelectedSpeaker] = useState('');
    const [testingMic, setTestingMic] = useState(false);
    const [previewStream, setPreviewStream] = useState(null);

    const meetingSession = useRef(null);
    const audioElement = useRef(null);
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const durationInterval = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const micStreamRef = useRef(null);
    const animationFrameRef = useRef(null);
    const previewVideoRef = useRef(null);

    // Load custom branding
    useEffect(() => {
        const domain = window.location.hostname;
        try {
            const cachedData = localStorage.getItem(`custom_images_${domain}`);
            const cachedImages = cachedData ? JSON.parse(cachedData) : {};
            if (cachedImages.logo) {
                setLogoUrl(cachedImages.logo);
            }
        } catch (error) {
            console.error('Erro ao carregar logo:', error);
        }
    }, []);

    // Load audio devices for test
    const loadAudioDevices = async () => {
        try {
            // Request permission first
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            stream.getTracks().forEach(track => track.stop());

            const devices = await navigator.mediaDevices.enumerateDevices();
            const inputs = devices.filter(d => d.kind === 'audioinput');
            const outputs = devices.filter(d => d.kind === 'audiooutput');

            setAudioDevices({ inputs, outputs });
            if (inputs.length > 0 && !selectedMic) setSelectedMic(inputs[0].deviceId);
            if (outputs.length > 0 && !selectedSpeaker) setSelectedSpeaker(outputs[0].deviceId);
        } catch (error) {
            console.error('Erro ao listar dispositivos:', error);
            setError('Permissao de audio/video negada. Por favor, permita o acesso ao microfone e camera.');
        }
    };

    // Start microphone test
    const startMicTest = async () => {
        try {
            setTestingMic(true);

            // Stop any existing stream
            if (micStreamRef.current) {
                micStreamRef.current.getTracks().forEach(track => track.stop());
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }

            // Get microphone stream
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { deviceId: selectedMic ? { exact: selectedMic } : undefined },
                video: true
            });

            micStreamRef.current = stream;
            setPreviewStream(stream);

            // Bind video preview
            if (previewVideoRef.current) {
                previewVideoRef.current.srcObject = stream;
            }

            // Create audio context for level visualization
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 256;

            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyserRef.current);

            // Start monitoring audio levels
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

            const checkAudioLevel = () => {
                if (!analyserRef.current) return;

                analyserRef.current.getByteFrequencyData(dataArray);
                const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
                const level = Math.min(100, (average / 128) * 100);

                setMicrophoneLevel(level);

                // Consider mic working if we get consistent audio above threshold
                if (level > 5) {
                    setIsMicWorking(true);
                }

                animationFrameRef.current = requestAnimationFrame(checkAudioLevel);
            };

            checkAudioLevel();
        } catch (error) {
            console.error('Erro ao testar microfone:', error);
            setTestingMic(false);
        }
    };

    // Stop microphone test
    const stopMicTest = () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach(track => track.stop());
            micStreamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        if (previewVideoRef.current) {
            previewVideoRef.current.srcObject = null;
        }
        setPreviewStream(null);
        setTestingMic(false);
        setMicrophoneLevel(0);
    };

    // Play test sound for speaker
    const playTestSound = async () => {
        try {
            // Create a simple beep sound using Web Audio API
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();

            // Try to set output device if supported
            if (selectedSpeaker && audioContext.setSinkId) {
                await audioContext.setSinkId(selectedSpeaker);
            }

            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 440; // A4 note
            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);

            // Play a second beep
            setTimeout(() => {
                const osc2 = audioContext.createOscillator();
                const gain2 = audioContext.createGain();
                osc2.connect(gain2);
                gain2.connect(audioContext.destination);
                osc2.frequency.value = 523.25; // C5 note
                osc2.type = 'sine';
                gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
                gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
                osc2.start(audioContext.currentTime);
                osc2.stop(audioContext.currentTime + 0.5);
            }, 300);

            setIsSpeakerTested(true);
        } catch (error) {
            console.error('Erro ao reproduzir som de teste:', error);
        }
    };

    // Confirm audio test and proceed to call
    const confirmAudioTest = () => {
        stopMicTest();
        setShowAudioTest(false);
        setAudioTestPassed(true);
    };

    // Skip audio test
    const skipAudioTest = () => {
        stopMicTest();
        setShowAudioTest(false);
    };

    // Load audio devices for in-call settings
    const loadCallAudioDevices = async () => {
        try {
            if (meetingSession.current) {
                const audioInputDevices = await meetingSession.current.audioVideo.listAudioInputDevices();
                const audioOutputDevices = await meetingSession.current.audioVideo.listAudioOutputDevices();
                setCallAudioDevices({
                    inputs: audioInputDevices,
                    outputs: audioOutputDevices
                });
                // Set current selections
                if (audioInputDevices.length > 0 && !callSelectedMic) {
                    setCallSelectedMic(audioInputDevices[0].deviceId);
                }
                if (audioOutputDevices.length > 0 && !callSelectedSpeaker) {
                    setCallSelectedSpeaker(audioOutputDevices[0].deviceId);
                }
            } else {
                // For mock mode or before session is ready
                const devices = await navigator.mediaDevices.enumerateDevices();
                const inputs = devices.filter(d => d.kind === 'audioinput');
                const outputs = devices.filter(d => d.kind === 'audiooutput');
                setCallAudioDevices({ inputs, outputs });
                if (inputs.length > 0 && !callSelectedMic) setCallSelectedMic(inputs[0].deviceId);
                if (outputs.length > 0 && !callSelectedSpeaker) setCallSelectedSpeaker(outputs[0].deviceId);
            }
        } catch (error) {
            console.error('Erro ao carregar dispositivos:', error);
        }
    };

    // Change microphone during call
    const changeCallMicrophone = async (deviceId) => {
        setCallSelectedMic(deviceId);
        if (meetingSession.current) {
            try {
                await meetingSession.current.audioVideo.startAudioInput(deviceId);
                console.log('Microfone alterado para:', deviceId);
            } catch (error) {
                console.error('Erro ao alterar microfone:', error);
            }
        }
    };

    // Change speaker during call
    const changeCallSpeaker = async (deviceId) => {
        setCallSelectedSpeaker(deviceId);
        if (meetingSession.current) {
            try {
                await meetingSession.current.audioVideo.chooseAudioOutput(deviceId);
                console.log('Alto-falante alterado para:', deviceId);
            } catch (error) {
                console.error('Erro ao alterar alto-falante:', error);
            }
        }
    };

    // Open settings modal
    const openSettings = async () => {
        await loadCallAudioDevices();
        setShowSettings(true);
    };

    // Enable audio manually (when autoplay is blocked)
    const enableAudio = async () => {
        if (audioElement.current) {
            try {
                audioElement.current.volume = 1;
                audioElement.current.muted = false;
                await audioElement.current.play();
                console.log('Audio enabled manually, volume:', audioElement.current.volume);
                setAudioBlocked(false);
            } catch (error) {
                console.error('Failed to enable audio:', error);
            }
        }
    };

    // Load devices when showing audio test
    useEffect(() => {
        if (showAudioTest) {
            loadAudioDevices();
        }
        return () => {
            stopMicTest();
        };
    }, [showAudioTest]);

    // Join meeting
    const joinMeeting = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            let response;

            if (isHost) {
                // Host (professional) joining
                if (urlCallId) {
                    response = await ApiService.send(
                        'post',
                        `${process.env.REACT_APP_URL_API}video/calls/${urlCallId}/rejoin`,
                        {},
                        true
                    );
                } else {
                    response = await ApiService.send(
                        'post',
                        `${process.env.REACT_APP_URL_API}video/attendances/${attendanceId}/calls`,
                        {},
                        true
                    );
                }
            } else {
                // Client joining
                response = await axios.post(
                    `${process.env.REACT_APP_URL_API}video/sala/${attendanceId}/join`,
                    { token }
                );
            }

            // Parse response - ApiService wraps in data, axios gets raw response
            const responseData = isHost
                ? response.data
                : (response.data?.data || response.data);

            const joinInfo = responseData?.host_join_info || responseData?.join_info || responseData;

            // ========== DIAGNÓSTICO: JoinInfo ==========
            console.log('╔══════════════════════════════════════════════════════════════╗');
            console.log('║               DIAGNÓSTICO CHIME - JOIN INFO                  ║');
            console.log('╚══════════════════════════════════════════════════════════════╝');
            console.log('[DIAG] Full responseData:', JSON.stringify(responseData, null, 2));
            console.log('[DIAG] MeetingId:', joinInfo?.meeting?.MeetingId);
            console.log('[DIAG] ExternalMeetingId:', joinInfo?.meeting?.ExternalMeetingId);
            console.log('[DIAG] MediaRegion:', joinInfo?.meeting?.MediaRegion);
            console.log('[DIAG] AttendeeId:', joinInfo?.attendee?.AttendeeId);
            console.log('[DIAG] ExternalUserId:', joinInfo?.attendee?.ExternalUserId);
            console.log('[DIAG] JoinToken present:', !!joinInfo?.attendee?.JoinToken);
            console.log('[DIAG] MediaPlacement:', JSON.stringify(joinInfo?.meeting?.MediaPlacement, null, 2));
            console.log('══════════════════════════════════════════════════════════════');

            if (!joinInfo?.meeting || !joinInfo?.attendee) {
                throw new Error('Dados da reunião não recebidos. Aguarde o profissional iniciar a chamada.');
            }

            // Save call_id from response
            const callIdFromResponse = responseData?.call?.call_id || responseData?.call_id;
            if (callIdFromResponse) {
                setCurrentCallId(callIdFromResponse);
                console.log('Call ID salvo:', callIdFromResponse);
            }

            setAttendanceInfo(responseData?.call?.attendance || responseData?.attendance || null);

            // Check if this is mock mode (backend returns mock URLs)
            const signalingUrl = joinInfo.meeting?.MediaPlacement?.SignalingUrl || '';
            const isMock = signalingUrl.includes('mock.chime.aws') || signalingUrl.includes('mock-');

            if (isMock) {
                console.log('🔧 Mock mode detected - skipping real Chime SDK connection');
                setIsMockMode(true);
                setIsConnected(true);
                setIsLoading(false);
                setIsWaitingForCall(false);

                // Clear retry interval if was waiting
                if (retryInterval.current) {
                    clearInterval(retryInterval.current);
                    retryInterval.current = null;
                }

                // Start duration timer for mock mode too
                durationInterval.current = setInterval(() => {
                    setCallDuration(prev => prev + 1);
                }, 1000);

                return; // Don't try to connect with real SDK
            }

            // Initialize Chime SDK (only for real mode)
            // DIAGNÓSTICO: Usando LogLevel.INFO para capturar mais logs do SDK
            const logger = new ConsoleLogger('VideoRoom', LogLevel.INFO);
            const deviceController = new DefaultDeviceController(logger);

            const configuration = new MeetingSessionConfiguration(
                joinInfo.meeting,
                joinInfo.attendee
            );

            meetingSession.current = new DefaultMeetingSession(
                configuration,
                logger,
                deviceController
            );

            // Expose to window for debugging
            window.meetingSession = meetingSession.current;
            window.audioElement = audioElement.current;

            // Função de diagnóstico global - pode ser chamada no console: window.chimeDiag()
            window.chimeDiag = () => {
                console.log('╔══════════════════════════════════════════════════════════════╗');
                console.log('║       DIAGNÓSTICO MANUAL - window.chimeDiag()                ║');
                console.log('╚══════════════════════════════════════════════════════════════╝');

                const ms = window.meetingSession;
                const ae = window.audioElement;

                if (!ms) {
                    console.error('[DIAG] meetingSession não encontrado!');
                    return;
                }

                console.log('[DIAG] MeetingId:', ms.configuration?.meetingId);
                console.log('[DIAG] AttendeeId:', ms.configuration?.credentials?.attendeeId);
                console.log('[DIAG] Mute local:', ms.audioVideo.realtimeIsLocalAudioMuted() ? '🔇 MUTADO' : '🔊 NÃO MUTADO');

                if (ae) {
                    console.log('[DIAG] audioElement:');
                    console.log('  - srcObject:', ae.srcObject ? 'PRESENTE' : 'NULL');
                    console.log('  - paused:', ae.paused);
                    console.log('  - muted:', ae.muted);
                    console.log('  - volume:', ae.volume);
                    console.log('  - readyState:', ae.readyState);

                    if (ae.srcObject) {
                        const tracks = ae.srcObject.getAudioTracks();
                        console.log('  - audioTracks:', tracks.length);
                        tracks.forEach((t, i) => {
                            console.log(`    Track ${i}: enabled=${t.enabled}, muted=${t.muted}, readyState=${t.readyState}`);
                        });
                    }
                } else {
                    console.error('[DIAG] audioElement não encontrado!');
                }

                console.log('\n[DIAG] Para forçar unmute: window.meetingSession.audioVideo.realtimeUnmuteLocalAudio()');
                console.log('[DIAG] Para rebind audio: window.meetingSession.audioVideo.bindAudioElement(window.audioElement)');
                console.log('[DIAG] Para forçar play: window.audioElement.play()');
            };

            console.log('[DIAG] 💡 Dica: Use window.chimeDiag() no console para diagnóstico manual');

            // Set up observers
            setupObservers();

            // Select devices first
            await selectDevices();

            // Start audio/video first
            console.log('[DIAG] Chamando audioVideo.start()...');
            await meetingSession.current.audioVideo.start();
            console.log('╔══════════════════════════════════════════════════════════════╗');
            console.log('║         ✅ audioVideo.start() CONCLUÍDO                       ║');
            console.log('╚══════════════════════════════════════════════════════════════╝');

            // Verificar estado do mute imediatamente após start
            const muteAfterStart = meetingSession.current.audioVideo.realtimeIsLocalAudioMuted();
            console.log('[DIAG] Estado mute IMEDIATAMENTE após start():', muteAfterStart ? '🔇 MUTADO' : '🔊 NÃO MUTADO');

            // Verificar se há áudio input ativo
            const activeAudioInput = meetingSession.current.audioVideo.getActiveAudioInputDevice?.();
            console.log('[DIAG] Dispositivo de áudio input ativo:', activeAudioInput);

            // Bind audio element AFTER starting with retry logic
            if (audioElement.current) {
                try {
                    console.log('╔══════════════════════════════════════════════════════════════╗');
                    console.log('║            DIAGNÓSTICO: BIND AUDIO ELEMENT                   ║');
                    console.log('╚══════════════════════════════════════════════════════════════╝');
                    console.log('[DIAG] audioElement existe:', !!audioElement.current);
                    console.log('[DIAG] audioElement tagName:', audioElement.current.tagName);
                    console.log('[DIAG] audioElement attributes:', {
                        autoplay: audioElement.current.autoplay,
                        playsInline: audioElement.current.playsInline,
                        muted: audioElement.current.muted
                    });

                    // First attempt to bind
                    const bindResult1 = await meetingSession.current.audioVideo.bindAudioElement(audioElement.current);
                    console.log('[DIAG] bindAudioElement resultado (1ª tentativa):', bindResult1);
                    console.log('[DIAG] srcObject após 1ª tentativa:', audioElement.current.srcObject);
                    if (audioElement.current.srcObject) {
                        const tracks = audioElement.current.srcObject.getAudioTracks();
                        console.log('[DIAG] Audio tracks no srcObject:', tracks.length);
                        tracks.forEach((t, i) => console.log(`[DIAG]   Track ${i}: ${t.label}, enabled: ${t.enabled}, muted: ${t.muted}`));
                    }

                    // If srcObject is still null, wait and retry
                    if (!audioElement.current.srcObject) {
                        console.warn('[DIAG] ⚠️ srcObject NULL após bind - aguardando 500ms...');
                        await new Promise(resolve => setTimeout(resolve, 500));
                        await meetingSession.current.audioVideo.bindAudioElement(audioElement.current);
                        console.log('[DIAG] srcObject após 2ª tentativa:', audioElement.current.srcObject);
                    }

                    // If still null, try unbind and rebind
                    if (!audioElement.current.srcObject) {
                        console.warn('[DIAG] ⚠️ srcObject ainda NULL - tentando unbind/rebind...');
                        meetingSession.current.audioVideo.unbindAudioElement();
                        await new Promise(resolve => setTimeout(resolve, 200));
                        await meetingSession.current.audioVideo.bindAudioElement(audioElement.current);
                        console.log('[DIAG] srcObject após 3ª tentativa (unbind/rebind):', audioElement.current.srcObject);
                    }

                    console.log('[DIAG] Estado final do audioElement:');
                    console.log('  - srcObject:', audioElement.current.srcObject ? 'PRESENTE ✅' : 'NULL ❌');
                    console.log('  - paused:', audioElement.current.paused);
                    console.log('  - volume:', audioElement.current.volume);
                    console.log('  - muted:', audioElement.current.muted);
                    console.log('  - readyState:', audioElement.current.readyState);

                    // Apply selected speaker to audio element (if supported)
                    if (selectedSpeaker && typeof audioElement.current.setSinkId === 'function') {
                        try {
                            await audioElement.current.setSinkId(selectedSpeaker);
                            console.log('Speaker aplicado:', selectedSpeaker);
                        } catch (sinkError) {
                            console.warn('Erro ao definir speaker:', sinkError);
                        }
                    }

                    // Force play audio (some browsers block autoplay)
                    try {
                        audioElement.current.volume = 1;
                        audioElement.current.muted = false;
                        await audioElement.current.play();
                        console.log('Audio element PLAYING - volume:', audioElement.current.volume, 'muted:', audioElement.current.muted, 'paused:', audioElement.current.paused);
                        setAudioBlocked(false);
                    } catch (playError) {
                        console.warn('AUTOPLAY BLOCKED - user interaction needed:', playError.message);
                        setAudioBlocked(true);
                    }
                } catch (audioError) {
                    console.error('Erro ao vincular audio:', audioError);
                }
            } else {
                console.error('AUDIO ELEMENT NAO ENCONTRADO!');
            }

            // Ensure local audio is unmuted
            console.log('[DIAG] Chamando realtimeUnmuteLocalAudio()...');
            const wasUnmuted = meetingSession.current.audioVideo.realtimeUnmuteLocalAudio();
            console.log('[DIAG] realtimeUnmuteLocalAudio() retornou:', wasUnmuted);
            const finalMuteState = meetingSession.current.audioVideo.realtimeIsLocalAudioMuted();
            console.log('[DIAG] Estado final do mute local:', finalMuteState ? '🔇 AINDA MUTADO ⚠️' : '🔊 DESMUTADO ✅');

            if (finalMuteState) {
                console.error('[DIAG] ❌ PROBLEMA CRÍTICO: Áudio local ainda está mutado após unmute!');
            }

            // Start local video
            await meetingSession.current.audioVideo.startLocalVideoTile();
            console.log('Video local iniciado');

            setIsConnected(true);
            setIsLoading(false);
            setIsWaitingForCall(false);

            // Clear retry interval if was waiting
            if (retryInterval.current) {
                clearInterval(retryInterval.current);
                retryInterval.current = null;
            }

            // Start duration timer
            durationInterval.current = setInterval(() => {
                setCallDuration(prev => prev + 1);
            }, 1000);

            // ========== DIAGNÓSTICO FINAL após 5 segundos ==========
            setTimeout(() => {
                console.log('╔══════════════════════════════════════════════════════════════╗');
                console.log('║       DIAGNÓSTICO FINAL (5 segundos após conexão)            ║');
                console.log('╚══════════════════════════════════════════════════════════════╝');

                if (meetingSession.current) {
                    const isMuted = meetingSession.current.audioVideo.realtimeIsLocalAudioMuted();
                    console.log('[DIAG] Estado mute local:', isMuted ? '🔇 MUTADO' : '🔊 NÃO MUTADO');
                }

                if (audioElement.current) {
                    console.log('[DIAG] audioElement:');
                    console.log('  - srcObject:', audioElement.current.srcObject ? 'PRESENTE' : 'NULL ⚠️');
                    console.log('  - paused:', audioElement.current.paused ? 'SIM ⚠️' : 'NÃO');
                    console.log('  - muted:', audioElement.current.muted ? 'SIM ⚠️' : 'NÃO');
                    console.log('  - volume:', audioElement.current.volume);
                    console.log('  - currentTime:', audioElement.current.currentTime);
                    console.log('  - readyState:', audioElement.current.readyState);

                    if (audioElement.current.srcObject) {
                        const tracks = audioElement.current.srcObject.getAudioTracks();
                        console.log('  - audioTracks:', tracks.length);
                        tracks.forEach((t, i) => {
                            console.log(`    Track ${i}: enabled=${t.enabled}, muted=${t.muted}, readyState=${t.readyState}`);
                        });
                    }
                }

                console.log('══════════════════════════════════════════════════════════════');
                console.log('RESUMO DO DIAGNÓSTICO:');
                console.log('1. Se audioPacketsSent > 0 mas outro não ouve: problema no publish ou MeetingId diferente');
                console.log('2. Se audioPacketsReceived > 0 mas você não ouve: problema no bindAudioElement ou autoplay');
                console.log('3. Se ambos = 0: sessão não conectou corretamente ao Chime');
                console.log('4. Se mute local = true: áudio não está sendo enviado');
                console.log('5. Se srcObject = null: bindAudioElement falhou');
                console.log('══════════════════════════════════════════════════════════════');
            }, 5000);

        } catch (error) {
            console.error('Erro ao entrar na chamada:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao entrar na chamada';

            // Host with "already active call" - try to get existing call and rejoin
            if (isHost && errorMessage.includes('Já existe uma chamada ativa')) {
                console.log('Chamada ativa encontrada, buscando para rejoin...');
                try {
                    // Get existing calls for this attendance
                    const callsResponse = await ApiService.send(
                        'get',
                        `${process.env.REACT_APP_URL_API}video/attendances/${attendanceId}/calls`,
                        {},
                        true
                    );

                    const calls = callsResponse?.data?.calls || [];
                    const activeCall = calls.find(c => c.status === 'created' || c.status === 'started');

                    if (activeCall) {
                        // Save call ID for ending later
                        setCurrentCallId(activeCall.call_id);
                        console.log('Call ID salvo (rejoin):', activeCall.call_id);

                        // Rejoin the active call
                        const rejoinResponse = await ApiService.send(
                            'post',
                            `${process.env.REACT_APP_URL_API}video/calls/${activeCall.call_id}/rejoin`,
                            {},
                            true
                        );

                        const joinInfo = rejoinResponse?.data?.join_info;
                        if (joinInfo?.meeting && joinInfo?.attendee) {
                            setAttendanceInfo(rejoinResponse?.data?.call?.attendance || null);

                            // Check mock mode
                            const signalingUrl = joinInfo.meeting?.MediaPlacement?.SignalingUrl || '';
                            const isMock = signalingUrl.includes('mock.chime.aws') || signalingUrl.includes('mock-');

                            if (isMock) {
                                setIsMockMode(true);
                                setIsConnected(true);
                                setIsLoading(false);
                                durationInterval.current = setInterval(() => {
                                    setCallDuration(prev => prev + 1);
                                }, 1000);
                                return;
                            }

                            // Connect to real Chime
                            console.log('[DIAG] REJOIN - Conectando ao Chime real...');
                            const logger = new ConsoleLogger('VideoRoom', LogLevel.INFO);
                            const deviceController = new DefaultDeviceController(logger);
                            const configuration = new MeetingSessionConfiguration(joinInfo.meeting, joinInfo.attendee);
                            meetingSession.current = new DefaultMeetingSession(configuration, logger, deviceController);

                            // Expose to window for debugging
                            window.meetingSession = meetingSession.current;
                            window.audioElement = audioElement.current;

                            setupObservers();
                            await selectDevices();
                            // Start first
                            await meetingSession.current.audioVideo.start();
                            console.log('[DIAG] REJOIN - audioVideo.start() concluído');

                            // Bind audio element AFTER starting
                            if (audioElement.current) {
                                await meetingSession.current.audioVideo.bindAudioElement(audioElement.current);
                                console.log('[DIAG] REJOIN - Audio vinculado, srcObject:', audioElement.current.srcObject);

                                // Force play audio
                                try {
                                    audioElement.current.volume = 1;
                                    audioElement.current.muted = false;
                                    await audioElement.current.play();
                                    console.log('[DIAG] REJOIN - Audio playing, volume:', audioElement.current.volume);
                                    setAudioBlocked(false);
                                } catch (playError) {
                                    console.warn('[DIAG] REJOIN - Autoplay blocked:', playError);
                                    setAudioBlocked(true);
                                }
                            }
                            // Ensure audio is unmuted
                            meetingSession.current.audioVideo.realtimeUnmuteLocalAudio();
                            const rejoinMuteState = meetingSession.current.audioVideo.realtimeIsLocalAudioMuted();
                            console.log('[DIAG] REJOIN - Estado mute após unmute:', rejoinMuteState ? '🔇 MUTADO' : '🔊 NÃO MUTADO');
                            await meetingSession.current.audioVideo.startLocalVideoTile();
                            setIsConnected(true);
                            setIsLoading(false);
                            durationInterval.current = setInterval(() => {
                                setCallDuration(prev => prev + 1);
                            }, 1000);
                            return;
                        }
                    }
                } catch (rejoinError) {
                    console.error('Erro ao fazer rejoin:', rejoinError);
                }
                setError('Já existe uma chamada ativa. Recarregue a página para tentar novamente.');
                setIsLoading(false);
                return;
            }

            // Check if it's a "waiting for call" situation (client only)
            if (!isHost && (errorMessage.includes('chamada ativa') || errorMessage.includes('Aguarde') || errorMessage.includes('profissional iniciar'))) {
                setIsWaitingForCall(true);
                setIsLoading(false);
                // Don't set error, we'll show waiting room
            } else {
                setError(errorMessage);
                setIsLoading(false);
            }
        }
    }, [attendanceId, urlCallId, token, isHost]);

    const setupObservers = () => {
        if (!meetingSession.current) return;

        // Audio/Video observer
        meetingSession.current.audioVideo.addObserver({
            audioVideoDidStart: () => {
                console.log('╔══════════════════════════════════════════════════════════════╗');
                console.log('║           ✅ AUDIO/VIDEO DID START - SESSÃO CONECTADA        ║');
                console.log('╚══════════════════════════════════════════════════════════════╝');

                // Verificar estado do mute IMEDIATAMENTE após start
                const isMuted = meetingSession.current.audioVideo.realtimeIsLocalAudioMuted();
                console.log('[DIAG] Estado mute local após start:', isMuted ? '🔇 MUTADO' : '🔊 NÃO MUTADO');

                // Ensure audio is unmuted after start
                if (isAudioOn) {
                    meetingSession.current.audioVideo.realtimeUnmuteLocalAudio();
                    const afterUnmute = meetingSession.current.audioVideo.realtimeIsLocalAudioMuted();
                    console.log('[DIAG] Estado mute após realtimeUnmuteLocalAudio:', afterUnmute ? '🔇 MUTADO' : '🔊 NÃO MUTADO');
                }

                // Verificar audioElement
                console.log('[DIAG] audioElement.current:', audioElement.current);
                console.log('[DIAG] audioElement.srcObject:', audioElement.current?.srcObject);
                console.log('[DIAG] audioElement.paused:', audioElement.current?.paused);
                console.log('[DIAG] audioElement.muted:', audioElement.current?.muted);
                console.log('[DIAG] audioElement.volume:', audioElement.current?.volume);
            },
            audioVideoDidStop: (sessionStatus) => {
                console.log('╔══════════════════════════════════════════════════════════════╗');
                console.log('║           ❌ AUDIO/VIDEO DID STOP                            ║');
                console.log('╚══════════════════════════════════════════════════════════════╝');
                console.log('[DIAG] Status code:', sessionStatus.statusCode());
            },
            audioVideoDidStartConnecting: (reconnecting) => {
                console.log('[DIAG] 🔄 Conectando... reconnecting:', reconnecting);
            },
            videoTileDidUpdate: (tileState) => {
                handleVideoTileUpdate(tileState);
            },
            videoTileWasRemoved: (tileId) => {
                console.log('[DIAG] Tile removido:', tileId);
            },
            // ========== DIAGNÓSTICO: Métricas de conexão ==========
            metricsDidReceive: (clientMetricReport) => {
                const metrics = clientMetricReport.getObservableMetrics();
                // Log apenas a cada 5 segundos para não poluir
                if (!window._lastMetricLog || Date.now() - window._lastMetricLog > 5000) {
                    window._lastMetricLog = Date.now();
                    console.log('╔══════════════════════════════════════════════════════════════╗');
                    console.log('║               MÉTRICAS DE CONEXÃO (a cada 5s)                ║');
                    console.log('╚══════════════════════════════════════════════════════════════╝');
                    console.log('[DIAG] audioPacketsSent:', metrics.audioPacketsSent);
                    console.log('[DIAG] audioPacketsReceived:', metrics.audioPacketsReceived);
                    console.log('[DIAG] audioPacketsReceivedFractionLoss:', metrics.audioPacketsReceivedFractionLoss);
                    console.log('[DIAG] audioSpeakerDelayMs:', metrics.audioSpeakerDelayMs);

                    // CRÍTICO: Se pacotes enviados > 0 mas recebidos = 0, problema de publish
                    if (metrics.audioPacketsSent > 0 && metrics.audioPacketsReceived === 0) {
                        console.warn('[DIAG] ⚠️ ÁUDIO ENVIADO MAS NÃO RECEBIDO - Verifique o outro participante');
                    }
                    // Se pacotes recebidos > 0 mas não ouve nada, problema de binding/playback
                    if (metrics.audioPacketsReceived > 0) {
                        console.log('[DIAG] ✅ Pacotes de áudio RECEBIDOS - Se não ouve, problema é no playback');
                    }
                }
            },
            connectionDidBecomePoor: () => {
                console.warn('[DIAG] ⚠️ Conexão ficou FRACA');
            },
            connectionDidSuggestStopVideo: () => {
                console.warn('[DIAG] ⚠️ Sugerido parar vídeo por conexão');
            }
        });

        // ========== DIAGNÓSTICO: Volume local ==========
        const localAttendeeId = meetingSession.current.configuration.credentials.attendeeId;
        console.log('[DIAG] Meu AttendeeId:', localAttendeeId);

        meetingSession.current.audioVideo.realtimeSubscribeToVolumeIndicator(
            localAttendeeId,
            (attendeeId, volume, muted, signalStrength) => {
                if (volume !== null && volume > 0) {
                    // Log apenas a cada 2 segundos
                    if (!window._lastLocalVolumeLog || Date.now() - window._lastLocalVolumeLog > 2000) {
                        window._lastLocalVolumeLog = Date.now();
                        console.log(`[DIAG] 🎤 ÁUDIO LOCAL - Volume: ${Math.round(volume * 100)}%, Muted: ${muted}, Signal: ${signalStrength}`);
                        if (muted) {
                            console.warn('[DIAG] ⚠️ ÁUDIO LOCAL ESTÁ MUTADO NO CHIME!');
                        }
                    }
                }
            }
        );

        // ========== DIAGNÓSTICO: Presença de participantes ==========
        meetingSession.current.audioVideo.realtimeSubscribeToAttendeeIdPresence(
            (attendeeId, present) => {
                console.log('╔══════════════════════════════════════════════════════════════╗');
                console.log(`║  ${present ? '✅ PARTICIPANTE ENTROU' : '❌ PARTICIPANTE SAIU'}                          ║`);
                console.log('╚══════════════════════════════════════════════════════════════╝');
                console.log('[DIAG] AttendeeId:', attendeeId);
                console.log('[DIAG] É participante local?', attendeeId === localAttendeeId);

                if (present && attendeeId !== localAttendeeId) {
                    console.log('[DIAG] 👤 Participante REMOTO entrou - inscrevendo no volume...');

                    // Subscribe to remote attendee's volume
                    meetingSession.current.audioVideo.realtimeSubscribeToVolumeIndicator(
                        attendeeId,
                        (id, volume, muted, signalStrength) => {
                            if (volume !== null && volume > 0.01) {
                                // Log quando áudio remoto é detectado
                                if (!window._lastRemoteVolumeLog || Date.now() - window._lastRemoteVolumeLog > 2000) {
                                    window._lastRemoteVolumeLog = Date.now();
                                    console.log(`[DIAG] 🔊 ÁUDIO REMOTO de ${id} - Volume: ${Math.round(volume * 100)}%, Muted: ${muted}`);

                                    // Se está recebendo áudio mas não ouve, problema é no binding/playback
                                    if (audioElement.current) {
                                        console.log('[DIAG] Estado do audioElement ao receber áudio remoto:');
                                        console.log('  - srcObject:', audioElement.current.srcObject ? 'PRESENTE' : 'NULL ⚠️');
                                        console.log('  - paused:', audioElement.current.paused ? 'SIM ⚠️' : 'NÃO');
                                        console.log('  - muted:', audioElement.current.muted ? 'SIM ⚠️' : 'NÃO');
                                        console.log('  - volume:', audioElement.current.volume);
                                    }
                                }
                            }
                        }
                    );
                } else if (!present && attendeeId !== localAttendeeId) {
                    console.log('[DIAG] 👤 Participante REMOTO saiu');
                }
            }
        );

        // ========== DIAGNÓSTICO: Eventos de mute/unmute ==========
        meetingSession.current.audioVideo.realtimeSubscribeToMuteAndUnmuteLocalAudio(
            (muted) => {
                console.log('╔══════════════════════════════════════════════════════════════╗');
                console.log(`║  EVENTO MUTE: Microfone local ${muted ? '🔇 MUTADO' : '🔊 DESMUTADO'}               ║`);
                console.log('╚══════════════════════════════════════════════════════════════╝');

                // Log stack trace para descobrir quem está mutando
                if (muted) {
                    console.log('[DIAG] Stack trace (quem mutou):', new Error().stack);
                }

                setIsAudioOn(!muted);
            }
        );

        console.log('[DIAG] ✅ Todos os observers configurados');
    };

    const handleVideoTileUpdate = (tileState) => {
        if (!meetingSession.current) return;

        if (tileState.localTile) {
            if (localVideoRef.current) {
                meetingSession.current.audioVideo.bindVideoElement(
                    tileState.tileId,
                    localVideoRef.current
                );
            }
        } else {
            if (remoteVideoRef.current) {
                meetingSession.current.audioVideo.bindVideoElement(
                    tileState.tileId,
                    remoteVideoRef.current
                );
            }
        }
    };

    const selectDevices = async () => {
        if (!meetingSession.current) return;

        console.log('╔══════════════════════════════════════════════════════════════╗');
        console.log('║            DIAGNÓSTICO: SELECT DEVICES                        ║');
        console.log('╚══════════════════════════════════════════════════════════════╝');

        const audioInputDevices = await meetingSession.current.audioVideo.listAudioInputDevices();
        const audioOutputDevices = await meetingSession.current.audioVideo.listAudioOutputDevices();
        const videoInputDevices = await meetingSession.current.audioVideo.listVideoInputDevices();

        console.log('[DIAG] Dispositivos de áudio entrada:', audioInputDevices.map(d => ({ id: d.deviceId.slice(0,8), label: d.label })));
        console.log('[DIAG] Dispositivos de áudio saída:', audioOutputDevices.map(d => ({ id: d.deviceId.slice(0,8), label: d.label })));
        console.log('[DIAG] Dispositivos de vídeo:', videoInputDevices.map(d => ({ id: d.deviceId.slice(0,8), label: d.label })));

        // Use user-selected devices from audio test, or fall back to first available
        if (audioInputDevices.length > 0) {
            const micId = selectedMic || audioInputDevices[0].deviceId;
            const micDevice = audioInputDevices.find(d => d.deviceId === micId) || audioInputDevices[0];
            console.log('[DIAG] Iniciando áudio input com:', micDevice.label || micDevice.deviceId);
            try {
                await meetingSession.current.audioVideo.startAudioInput(micDevice.deviceId);
                console.log('[DIAG] ✅ startAudioInput SUCCESS');
            } catch (audioInputError) {
                console.error('[DIAG] ❌ startAudioInput FAILED:', audioInputError);
            }
        } else {
            console.warn('[DIAG] ⚠️ Nenhum dispositivo de áudio de entrada encontrado!');
        }

        if (audioOutputDevices.length > 0) {
            const speakerId = selectedSpeaker || audioOutputDevices[0].deviceId;
            const speakerDevice = audioOutputDevices.find(d => d.deviceId === speakerId) || audioOutputDevices[0];
            console.log('[DIAG] Selecionando áudio output:', speakerDevice.label || speakerDevice.deviceId);
            try {
                await meetingSession.current.audioVideo.chooseAudioOutput(speakerDevice.deviceId);
                console.log('[DIAG] ✅ chooseAudioOutput SUCCESS');
            } catch (audioOutputError) {
                console.error('[DIAG] ❌ chooseAudioOutput FAILED:', audioOutputError);
            }
        }

        if (videoInputDevices.length > 0) {
            await meetingSession.current.audioVideo.startVideoInput(videoInputDevices[0].deviceId);
            console.log('[DIAG] ✅ startVideoInput SUCCESS');
        }
    };

    const toggleAudio = async () => {
        if (!meetingSession.current) return;

        if (isAudioOn) {
            meetingSession.current.audioVideo.realtimeMuteLocalAudio();
        } else {
            meetingSession.current.audioVideo.realtimeUnmuteLocalAudio();
        }
        setIsAudioOn(!isAudioOn);
    };

    const toggleVideo = async () => {
        if (!meetingSession.current) return;

        if (isVideoOn) {
            meetingSession.current.audioVideo.stopLocalVideoTile();
        } else {
            await meetingSession.current.audioVideo.startLocalVideoTile();
        }
        setIsVideoOn(!isVideoOn);
    };

    // Cleanup all media streams and resources
    const cleanupMediaResources = () => {
        console.log('Limpando recursos de midia...');

        // Stop Chime SDK audio/video
        if (meetingSession.current) {
            try {
                meetingSession.current.audioVideo.stopLocalVideoTile();
                meetingSession.current.audioVideo.stopAudioInput();
                meetingSession.current.audioVideo.stopVideoInput();
                meetingSession.current.audioVideo.stop();
                console.log('Chime SDK parado');
            } catch (e) {
                console.error('Erro ao parar Chime SDK:', e);
            }
            meetingSession.current = null;
        }

        // Stop audio element
        if (audioElement.current) {
            audioElement.current.srcObject = null;
        }

        // Stop local video
        if (localVideoRef.current && localVideoRef.current.srcObject) {
            localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
            localVideoRef.current.srcObject = null;
        }

        // Stop remote video
        if (remoteVideoRef.current && remoteVideoRef.current.srcObject) {
            remoteVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
            remoteVideoRef.current.srcObject = null;
        }

        // Stop preview stream (from audio test)
        if (previewStream) {
            previewStream.getTracks().forEach(track => track.stop());
            setPreviewStream(null);
        }

        // Stop mic test stream
        if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach(track => track.stop());
            micStreamRef.current = null;
        }

        // Close audio context
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }

        // Cancel animation frame
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        // Clear intervals
        if (durationInterval.current) {
            clearInterval(durationInterval.current);
            durationInterval.current = null;
        }

        if (retryInterval.current) {
            clearInterval(retryInterval.current);
            retryInterval.current = null;
        }

        console.log('Recursos de midia limpos');
    };

    const endCall = async () => {
        const result = await Swal.fire({
            title: 'Encerrar chamada?',
            text: 'Deseja realmente encerrar esta chamada?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, encerrar',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            // Cleanup all media resources first
            cleanupMediaResources();

            // End call on backend if we have a call ID
            if (currentCallId) {
                try {
                    await ApiService.send(
                        'post',
                        `${process.env.REACT_APP_URL_API}video/calls/${currentCallId}/end`,
                        {},
                        true
                    );
                    console.log('Chamada encerrada no backend:', currentCallId);
                } catch (endError) {
                    console.error('Erro ao encerrar chamada no backend:', endError);
                    // Continue anyway - user wants to leave
                }
            }

            Swal.fire({
                icon: 'success',
                title: 'Chamada encerrada',
                text: `Duração: ${formatDuration(callDuration)}`,
                timer: 3000,
                showConfirmButton: false
            });

            // Navigate back
            if (isHost) {
                navigate('/admin/video/queue');
            } else {
                navigate('/video');
            }
        } catch (error) {
            console.error('Erro ao encerrar chamada:', error);
        }
    };

    const formatDuration = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        const parts = [];
        if (hrs > 0) parts.push(`${hrs}h`);
        if (mins > 0) parts.push(`${mins}min`);
        parts.push(`${secs}s`);

        return parts.join(' ');
    };

    // Effect to join meeting on mount (only after audio test is completed/skipped)
    useEffect(() => {
        if (!showAudioTest) {
            joinMeeting();
        }

        return () => {
            // Cleanup all media resources when component unmounts
            cleanupMediaResources();
        };
    }, [joinMeeting, showAudioTest]);

    // Auto-retry when waiting for call
    useEffect(() => {
        if (isWaitingForCall && !isConnected) {
            retryInterval.current = setInterval(() => {
                console.log('Tentando conectar novamente...');
                joinMeeting();
            }, 5000); // Retry every 5 seconds

            return () => {
                if (retryInterval.current) {
                    clearInterval(retryInterval.current);
                }
            };
        }
    }, [isWaitingForCall, isConnected, joinMeeting]);

    // Audio test screen (similar to Google Meet)
    if (showAudioTest) {
        return (
            <div className="audio-test-screen">
                <Card className="audio-test-card">
                    <div className="text-center mb-3 mb-md-4">
                        {logoUrl && (
                            <img src={logoUrl} alt="Logo" className="mb-3" style={{ height: '40px', maxWidth: '150px', objectFit: 'contain' }} />
                        )}
                        <h4 className="text-white mb-2">
                            <i className="fa fa-cog me-2 d-none d-sm-inline"></i>
                            Configurar Audio e Video
                        </h4>
                        <p className="text-white-50 mb-0 small d-none d-md-block">
                            Verifique se seu microfone, alto-falante e camera estao funcionando antes de entrar na chamada
                        </p>
                    </div>

                    <Row>
                        {/* Video Preview */}
                        <Col md={6} className="mb-3 mb-md-4">
                            <div className="audio-test-preview">
                                <video
                                    ref={previewVideoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                />
                                {!previewStream && (
                                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                                        <i className="fa fa-video-camera fa-2x fa-md-3x text-white-50 mb-2"></i>
                                        <p className="text-white-50 mb-0 small">Clique em "Testar" para ver o preview</p>
                                    </div>
                                )}
                            </div>
                        </Col>

                        {/* Audio Controls */}
                        <Col md={6}>
                            {/* Microphone Section */}
                            <div className="mb-3 mb-md-4">
                                <label className="text-white mb-2 d-flex align-items-center small">
                                    <i className="fa fa-microphone me-2"></i>
                                    Microfone
                                    {isMicWorking && <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>OK</span>}
                                </label>
                                <select
                                    className="form-select form-select-sm mb-2 device-select"
                                    value={selectedMic}
                                    onChange={(e) => {
                                        setSelectedMic(e.target.value);
                                        setIsMicWorking(false);
                                        if (testingMic) {
                                            stopMicTest();
                                            setTimeout(() => startMicTest(), 100);
                                        }
                                    }}
                                >
                                    {audioDevices.inputs.map(device => (
                                        <option key={device.deviceId} value={device.deviceId}>
                                            {device.label || `Microfone ${device.deviceId.slice(0, 5)}`}
                                        </option>
                                    ))}
                                </select>

                                {/* Microphone Level Indicator */}
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <div className="audio-level-bar flex-grow-1">
                                        <div
                                            className="audio-level-fill"
                                            style={{
                                                width: `${microphoneLevel}%`,
                                                backgroundColor: microphoneLevel > 50 ? '#27ae60' : microphoneLevel > 20 ? '#f39c12' : '#3498db'
                                            }}
                                        />
                                    </div>
                                    <small className="text-white-50" style={{ width: '35px', fontSize: '0.7rem' }}>{Math.round(microphoneLevel)}%</small>
                                </div>

                                <button
                                    className={`btn btn-sm w-100 ${testingMic ? 'btn-danger' : 'btn-outline-light'}`}
                                    onClick={testingMic ? stopMicTest : startMicTest}
                                >
                                    <i className={`fa ${testingMic ? 'fa-stop' : 'fa-play'} me-2`}></i>
                                    {testingMic ? 'Parar' : 'Testar'}
                                </button>

                                {testingMic && !isMicWorking && (
                                    <small className="text-warning d-block mt-2" style={{ fontSize: '0.7rem' }}>
                                        <i className="fa fa-info-circle me-1"></i>
                                        Fale algo para testar...
                                    </small>
                                )}
                            </div>

                            {/* Speaker Section */}
                            <div className="mb-3">
                                <label className="text-white mb-2 d-flex align-items-center small">
                                    <i className="fa fa-volume-up me-2"></i>
                                    Alto-falante
                                    {isSpeakerTested && <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>OK</span>}
                                </label>
                                <select
                                    className="form-select form-select-sm mb-2 device-select"
                                    value={selectedSpeaker}
                                    onChange={(e) => {
                                        setSelectedSpeaker(e.target.value);
                                        setIsSpeakerTested(false);
                                    }}
                                >
                                    {audioDevices.outputs.length > 0 ? (
                                        audioDevices.outputs.map(device => (
                                            <option key={device.deviceId} value={device.deviceId}>
                                                {device.label || `Alto-falante ${device.deviceId.slice(0, 5)}`}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="">Alto-falante padrao</option>
                                    )}
                                </select>

                                <button
                                    className="btn btn-sm btn-outline-light w-100"
                                    onClick={playTestSound}
                                >
                                    <i className="fa fa-play me-2"></i>
                                    Testar Som
                                </button>
                            </div>
                        </Col>
                    </Row>

                    {/* Action Buttons */}
                    <div className="audio-test-actions d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center mt-3 pt-3" style={{ borderTop: '1px solid #333' }}>
                        <button
                            className="btn btn-link text-white-50 order-2 order-sm-1 mt-2 mt-sm-0"
                            onClick={skipAudioTest}
                        >
                            Pular teste
                        </button>

                        <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-center order-1 order-sm-2">
                            {(!isMicWorking || !isSpeakerTested) && (
                                <small className="text-warning text-center text-sm-end d-none d-md-block" style={{ fontSize: '0.7rem' }}>
                                    <i className="fa fa-exclamation-triangle me-1"></i>
                                    {!isMicWorking && !isSpeakerTested
                                        ? 'Teste mic e audio'
                                        : !isMicWorking
                                            ? 'Teste o mic'
                                            : 'Teste o audio'}
                                </small>
                            )}
                            <button
                                className="btn btn-success"
                                onClick={confirmAudioTest}
                            >
                                <i className="fa fa-phone me-2"></i>
                                Entrar na Chamada
                            </button>
                        </div>
                    </div>

                    {/* Tips - Hidden on mobile */}
                    <div className="audio-test-tips mt-3 p-3 rounded d-none d-md-block" style={{ backgroundColor: '#0f0f23' }}>
                        <h6 className="text-white mb-2 small">
                            <i className="fa fa-lightbulb-o me-2"></i>
                            Dicas:
                        </h6>
                        <ul className="text-white-50 mb-0" style={{ fontSize: '0.75rem' }}>
                            <li>Use fones de ouvido para evitar eco</li>
                            <li>Fique em um ambiente silencioso</li>
                            <li>Verifique as permissoes do navegador</li>
                        </ul>
                    </div>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="video-page-container d-flex align-items-center justify-content-center">
                <Card className="p-5 text-center">
                    <i className="fa fa-exclamation-circle fa-4x text-danger mb-4"></i>
                    <h4>Erro ao conectar</h4>
                    <p className="text-muted">{error}</p>
                    <button
                        className="btn btn-primary mt-3"
                        onClick={() => window.location.reload()}
                    >
                        <i className="fa fa-refresh me-2"></i> Tentar Novamente
                    </button>
                </Card>
            </div>
        );
    }

    if (isWaitingForCall) {
        return (
            <div className="video-page-container d-flex align-items-center justify-content-center" style={{ backgroundColor: '#1a1a2e', minHeight: '100vh' }}>
                <Card className="p-5 text-center" style={{ maxWidth: '500px' }}>
                    {logoUrl && (
                        <img src={logoUrl} alt="Logo" style={{ height: '60px', marginBottom: '30px' }} />
                    )}
                    <div className="video-waiting-animation mb-4">
                        <i className="fa fa-hourglass-half fa-3x text-primary"></i>
                    </div>
                    <h4 className="text-primary">Sala de Espera</h4>
                    <p className="text-muted mb-4">
                        Aguarde o profissional iniciar a chamada de vídeo.<br />
                        Você será conectado automaticamente.
                    </p>
                    <div className="d-flex justify-content-center align-items-center mb-3">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                            <span className="visually-hidden">Aguardando...</span>
                        </div>
                        <small className="text-muted">Verificando a cada 5 segundos...</small>
                    </div>
                    <hr />
                    <p className="text-muted small mb-0">
                        <i className="fa fa-info-circle me-1"></i>
                        Mantenha esta página aberta. Você será notificado quando a chamada iniciar.
                    </p>
                </Card>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="video-page-container d-flex align-items-center justify-content-center">
                <Card className="p-5 text-center">
                    <div className="video-waiting-animation mb-4">
                        <i className="fa fa-video-camera fa-2x text-white"></i>
                    </div>
                    <h4>Conectando...</h4>
                    <p className="text-muted">Preparando sua chamada de vídeo</p>
                </Card>
            </div>
        );
    }

    // Mock mode - Development UI (no real video connection)
    if (isMockMode) {
        return (
            <div className="video-room-container">
                {/* Header */}
                <div className="video-room-header d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center header-info">
                        {logoUrl && (
                            <img src={logoUrl} alt="Logo" className="header-logo" />
                        )}
                        <div>
                            <h5 className="header-title text-white">
                                <i className="fa fa-video-camera me-2"></i>
                                Chamada de Video
                                <span className="badge bg-warning ms-2 d-none d-sm-inline" style={{ fontSize: '10px' }}>DEV</span>
                            </h5>
                            {attendanceInfo && (
                                <small className="header-subtitle text-white-50">
                                    {attendanceInfo.client?.name || 'Cliente'}
                                </small>
                            )}
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 header-status">
                        <span className="video-status-badge bg-warning text-dark d-none d-md-inline-flex">
                            <i className="fa fa-code me-1"></i>
                            Mock
                        </span>
                        <span className="video-call-timer">
                            <i className="fa fa-clock me-1"></i>
                            {formatDuration(callDuration)}
                        </span>
                    </div>
                </div>

                {/* Mock Video Area */}
                <div className="video-main-container">
                    {/* Development Mode Banner - Hidden on small screens */}
                    <div className="alert alert-warning d-none d-md-flex align-items-center mb-3" role="alert" style={{ maxWidth: '1200px', margin: '0 auto 16px' }}>
                        <i className="fa fa-exclamation-triangle me-3"></i>
                        <div>
                            <strong>Modo de Desenvolvimento</strong>
                            <p className="mb-0 small">
                                O servidor esta em mock mode. As chamadas de video reais requerem credenciais AWS Chime configuradas.
                            </p>
                        </div>
                    </div>

                    <div className="video-area">
                        <div className="video-remote-container">
                            {/* Mock Remote Video */}
                            <div className="video-remote-placeholder">
                                <div className="video-remote-avatar" style={{ backgroundColor: '#3498db' }}>
                                    {(attendanceInfo?.client?.name || 'C').charAt(0).toUpperCase()}
                                </div>
                                <p className="text-white mb-1">
                                    {attendanceInfo?.client?.name || 'Participante Remoto'}
                                </p>
                                <small className="text-white-50">
                                    <i className="fa fa-video-camera me-1"></i>
                                    Video simulado
                                </small>
                            </div>

                            {/* Mock Local Video (Picture-in-Picture) */}
                            <div className="video-local-container">
                                <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-circle"
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: isVideoOn ? '#27ae60' : '#e74c3c',
                                            color: 'white',
                                            fontSize: '1rem'
                                        }}
                                    >
                                        {isHost ? 'P' : 'V'}
                                    </div>
                                    <small className="text-white-50 mt-1" style={{ fontSize: '0.7rem' }}>
                                        {isHost ? 'Profissional' : 'Voce'}
                                    </small>
                                    {!isVideoOn && (
                                        <small className="text-danger" style={{ fontSize: '0.65rem' }}>
                                            <i className="fa fa-video-slash"></i>
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="video-controls">
                        <button
                            className={`video-control-btn ${isAudioOn ? 'active' : 'inactive'}`}
                            onClick={() => setIsAudioOn(!isAudioOn)}
                            title={isAudioOn ? 'Desativar microfone' : 'Ativar microfone'}
                        >
                            <i className={`fa ${isAudioOn ? 'fa-microphone' : 'fa-microphone-slash'}`}></i>
                        </button>

                        <button
                            className={`video-control-btn ${isVideoOn ? 'active' : 'inactive'}`}
                            onClick={() => setIsVideoOn(!isVideoOn)}
                            title={isVideoOn ? 'Desativar camera' : 'Ativar camera'}
                        >
                            <i className={`fa ${isVideoOn ? 'fa-video-camera' : 'fa-video-slash'}`}></i>
                        </button>

                        <button
                            className="video-control-btn settings"
                            onClick={openSettings}
                            title="Configuracoes de audio"
                        >
                            <i className="fa fa-cog"></i>
                        </button>

                        <button
                            className="video-control-btn end-call"
                            onClick={endCall}
                            title="Encerrar chamada"
                        >
                            <i className="fa fa-phone" style={{ transform: 'rotate(135deg)' }}></i>
                        </button>
                    </div>
                </div>

                {/* Settings Modal */}
                {showSettings && (
                    <div className="video-settings-overlay" onClick={() => setShowSettings(false)}>
                        <div className="video-settings-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="video-settings-header">
                                <h5 className="mb-0">
                                    <i className="fa fa-cog me-2"></i>
                                    Configuracoes de Audio
                                </h5>
                                <button className="btn-close btn-close-white" onClick={() => setShowSettings(false)}></button>
                            </div>
                            <div className="video-settings-body">
                                {/* Microphone */}
                                <div className="mb-4">
                                    <label className="text-white mb-2 d-flex align-items-center">
                                        <i className="fa fa-microphone me-2"></i>
                                        Microfone
                                    </label>
                                    <select
                                        className="form-select device-select"
                                        value={callSelectedMic}
                                        onChange={(e) => changeCallMicrophone(e.target.value)}
                                    >
                                        {callAudioDevices.inputs.map(device => (
                                            <option key={device.deviceId} value={device.deviceId}>
                                                {device.label || `Microfone ${device.deviceId.slice(0, 5)}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Speaker */}
                                <div className="mb-3">
                                    <label className="text-white mb-2 d-flex align-items-center">
                                        <i className="fa fa-volume-up me-2"></i>
                                        Alto-falante
                                    </label>
                                    <select
                                        className="form-select device-select"
                                        value={callSelectedSpeaker}
                                        onChange={(e) => changeCallSpeaker(e.target.value)}
                                    >
                                        {callAudioDevices.outputs.length > 0 ? (
                                            callAudioDevices.outputs.map(device => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || `Alto-falante ${device.deviceId.slice(0, 5)}`}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="">Alto-falante padrao</option>
                                        )}
                                    </select>
                                </div>

                                <button
                                    className="btn btn-outline-light w-100"
                                    onClick={playTestSound}
                                >
                                    <i className="fa fa-play me-2"></i>
                                    Testar Som
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="video-room-container">
            {/* Hidden audio element */}
            <audio ref={audioElement} autoPlay playsInline muted={false} style={{ display: 'none' }} />

            {/* Header */}
            <div className="video-room-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center header-info">
                    {logoUrl && (
                        <img src={logoUrl} alt="Logo" className="header-logo" />
                    )}
                    <div>
                        <h5 className="header-title text-white">
                            <i className="fa fa-video-camera me-2"></i>
                            Chamada de Video
                        </h5>
                        {attendanceInfo && (
                            <small className="header-subtitle text-white-50">
                                {attendanceInfo.client?.name || 'Cliente'}
                            </small>
                        )}
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2 header-status">
                    <span className="video-status-badge bg-success text-white">
                        <i className="fa fa-circle me-1" style={{ fontSize: '6px' }}></i>
                        Conectado
                    </span>
                    <span className="video-call-timer">
                        <i className="fa fa-clock me-1"></i>
                        {formatDuration(callDuration)}
                    </span>
                </div>
            </div>

            {/* Video Area */}
            <div className="video-main-container">
                <div className="video-area">
                    <div className="video-remote-container">
                        <video
                            ref={remoteVideoRef}
                            className="video-remote"
                            autoPlay
                            playsInline
                        />
                        {!remoteVideoRef.current?.srcObject && (
                            <div className="video-remote-placeholder">
                                <div className="video-remote-avatar">
                                    {(attendanceInfo?.client?.name || 'C').charAt(0).toUpperCase()}
                                </div>
                                <p className="text-white-50">Aguardando video...</p>
                            </div>
                        )}

                        {/* Local Video (Picture-in-Picture) */}
                        <div className="video-local-container">
                            <video
                                ref={localVideoRef}
                                className="video-local"
                                autoPlay
                                playsInline
                                muted
                            />
                            {!isVideoOn && (
                                <div className="video-local-off">
                                    <i className="fa fa-video-slash fa-lg text-white-50"></i>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Audio Blocked Warning */}
                {audioBlocked && (
                    <div className="audio-blocked-alert" onClick={enableAudio}>
                        <i className="fa fa-volume-off me-2"></i>
                        <span>Clique aqui para ativar o audio</span>
                    </div>
                )}

                {/* Controls */}
                <div className="video-controls">
                    {audioBlocked && (
                        <button
                            className="video-control-btn audio-blocked"
                            onClick={enableAudio}
                            title="Clique para ativar audio"
                        >
                            <i className="fa fa-volume-up"></i>
                        </button>
                    )}

                    <button
                        className={`video-control-btn ${isAudioOn ? 'active' : 'inactive'}`}
                        onClick={toggleAudio}
                        title={isAudioOn ? 'Desativar microfone' : 'Ativar microfone'}
                    >
                        <i className={`fa ${isAudioOn ? 'fa-microphone' : 'fa-microphone-slash'}`}></i>
                    </button>

                    <button
                        className={`video-control-btn ${isVideoOn ? 'active' : 'inactive'}`}
                        onClick={toggleVideo}
                        title={isVideoOn ? 'Desativar camera' : 'Ativar camera'}
                    >
                        <i className={`fa ${isVideoOn ? 'fa-video-camera' : 'fa-video-slash'}`}></i>
                    </button>

                    <button
                        className="video-control-btn settings"
                        onClick={openSettings}
                        title="Configuracoes de audio"
                    >
                        <i className="fa fa-cog"></i>
                    </button>

                    <button
                        className="video-control-btn end-call"
                        onClick={endCall}
                        title="Encerrar chamada"
                    >
                        <i className="fa fa-phone" style={{ transform: 'rotate(135deg)' }}></i>
                    </button>
                </div>
            </div>

            {/* Settings Modal */}
            {showSettings && (
                <div className="video-settings-overlay" onClick={() => setShowSettings(false)}>
                    <div className="video-settings-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="video-settings-header">
                            <h5 className="mb-0">
                                <i className="fa fa-cog me-2"></i>
                                Configuracoes de Audio
                            </h5>
                            <button className="btn-close btn-close-white" onClick={() => setShowSettings(false)}></button>
                        </div>
                        <div className="video-settings-body">
                            {/* Microphone */}
                            <div className="mb-4">
                                <label className="text-white mb-2 d-flex align-items-center">
                                    <i className="fa fa-microphone me-2"></i>
                                    Microfone
                                </label>
                                <select
                                    className="form-select device-select"
                                    value={callSelectedMic}
                                    onChange={(e) => changeCallMicrophone(e.target.value)}
                                >
                                    {callAudioDevices.inputs.map(device => (
                                        <option key={device.deviceId} value={device.deviceId}>
                                            {device.label || `Microfone ${device.deviceId.slice(0, 5)}`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Speaker */}
                            <div className="mb-3">
                                <label className="text-white mb-2 d-flex align-items-center">
                                    <i className="fa fa-volume-up me-2"></i>
                                    Alto-falante
                                </label>
                                <select
                                    className="form-select device-select"
                                    value={callSelectedSpeaker}
                                    onChange={(e) => changeCallSpeaker(e.target.value)}
                                >
                                    {callAudioDevices.outputs.length > 0 ? (
                                        callAudioDevices.outputs.map(device => (
                                            <option key={device.deviceId} value={device.deviceId}>
                                                {device.label || `Alto-falante ${device.deviceId.slice(0, 5)}`}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="">Alto-falante padrao</option>
                                    )}
                                </select>
                            </div>

                            <button
                                className="btn btn-outline-light w-100"
                                onClick={playTestSound}
                            >
                                <i className="fa fa-play me-2"></i>
                                Testar Som
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VideoRoom;
